"use client";

import { ResponsiveLabel } from "@/components/ResponsiveLabel";
import { SqarpLogo } from "@/components/pro-blocks/sqarp-logo";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { ChevronDown, Menu, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";

type NavItem = {
  label: string;
  href: string;
};

type NavAction = {
  label?: string;
  mobileLabel?: string;
  href?: string;
};

type ResponsiveNavLink = {
  label?: string;
  mobileLabel?: string;
  href: string;
};

type NavMenuGroup = {
  solutionsLabel?: string;
  solutionsItems?: readonly NavItem[];
  mainMenuItems?: readonly NavItem[];
};

type NavActionGroup = {
  loginLabel?: string;
  loginHref?: string;
  ctaLink?: NavAction;
};

export interface LpNavbar3Props extends NavMenuGroup, NavActionGroup {}

interface NavMenuItemsProps extends NavMenuGroup {
  className?: string;
}

interface MobileSolutionsItemsProps extends Pick<
  NavMenuGroup,
  "solutionsLabel" | "solutionsItems"
> {
  onNavigate: () => void;
}

const hasItems = (items?: readonly NavItem[]): items is readonly NavItem[] =>
  Boolean(items?.length);

const toResponsiveLink = ({
  label,
  mobileLabel,
  href,
}: NavAction): ResponsiveNavLink | undefined =>
  href && (label || mobileLabel) ? { label, mobileLabel, href } : undefined;

const toActionLink = (link?: NavAction): ResponsiveNavLink | undefined =>
  link ? toResponsiveLink(link) : undefined;

const SolutionsDisclosure = ({
  label,
  items,
  className,
}: {
  label: string;
  items: readonly NavItem[];
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setOpen(false);
      const trigger = rootRef.current?.querySelector("button");
      if (trigger instanceof HTMLButtonElement) {
        queueMicrotask(() => trigger.focus());
      }
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onFocusIn = (e: FocusEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener("keydown", onEscape);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusin", onFocusIn, true);
    return () => {
      document.removeEventListener("keydown", onEscape);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusin", onFocusIn, true);
    };
  }, [open]);

  return (
    <div className={cn("relative", className)} ref={rootRef}>
      <Button
        type="button"
        variant="ghost-no-active"
        className="w-full gap-0.5 lg:w-auto"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <ChevronDown
          className={cn(
            "size-4 shrink-0 transition-transform duration-200",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </Button>
      {open ? (
        <div
          className="ring-foreground/10 bg-popover text-popover-foreground absolute top-full left-1/2 z-80 mt-1 min-w-64 w-56 -translate-x-1/2 rounded-xs p-1 shadow-md ring-1"
        >
          <nav id={panelId} aria-label={label}>
            <div className="flex flex-col gap-0.5 p-0">
              {items.map(({ label, href }) => (
                <Link
                  key={href + label}
                  className="text-foreground block rounded-sm px-3 py-2.5 text-base no-underline outline-none hover:underline focus-visible:underline"
                  href={href}
                  onClick={() => setOpen(false)}
                >
                  {label}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      ) : null}
    </div>
  );
};

const NavMenuItems = ({
  solutionsLabel,
  solutionsItems,
  mainMenuItems,
  className,
}: NavMenuItemsProps) => (
  <div
    className={`flex flex-col items-center gap-1 lg:flex-row lg:gap-0.5 ${className ?? ""}`}
  >
    {solutionsLabel && solutionsItems?.length ? (
      <SolutionsDisclosure
        label={solutionsLabel}
        items={solutionsItems}
        className="w-full lg:w-auto"
      />
    ) : null}
    {mainMenuItems?.map(({ label, href }) => (
      <Button
        key={label}
        asChild
        variant="ghost"
        size="lg"
        className="w-full lg:w-auto"
      >
        <Link href={href} className="w-full lg:w-auto hover:underline">
          {label}
        </Link>
      </Button>
    ))}
  </div>
);

const MobileSolutionsItems = ({
  solutionsLabel,
  solutionsItems,
  onNavigate,
}: MobileSolutionsItemsProps) =>
  solutionsLabel && hasItems(solutionsItems) ? (
    <div className="w-full rounded-xs bg-muted/40 p-4 md:rounded-lg">
      <p className="px-2 pb-3 pt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {solutionsLabel}
      </p>
      <div className="flex flex-col gap-1">
        {solutionsItems.map(({ label, href }) => (
          <Button
            key={label}
            asChild
            variant="ghost"
            className="w-full justify-start max-md:text-lg"
          >
            <Link href={href} className="hover:underline" onClick={onNavigate}>
              {label}
            </Link>
          </Button>
        ))}
      </div>
    </div>
  ) : null;

export function LpNavbar3({
  solutionsLabel,
  solutionsItems,
  mainMenuItems,
  loginLabel,
  loginHref,
  ctaLink,
}: LpNavbar3Props) {
  const solutionMenuItems = hasItems(solutionsItems) ? solutionsItems : undefined;
  const primaryMenuItems = hasItems(mainMenuItems) ? mainMenuItems : undefined;
  const loginLink = toResponsiveLink({ label: loginLabel, href: loginHref });
  const ctaAction = toActionLink(ctaLink);

  const hasSolutionsMenu = Boolean(solutionsLabel && solutionMenuItems);
  const hasMainMenuItems = Boolean(primaryMenuItems);
  const hasDesktopMenu = hasSolutionsMenu || hasMainMenuItems;
  const hasMobileMenuContent =
    hasDesktopMenu || Boolean(loginLink) || Boolean(ctaAction);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [isAtTop, setIsAtTop] = useState(true);
  const lastScrollY = useRef(0);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsAtTop(currentScrollY <= 0);

      if (isMenuOpen) {
        setIsNavVisible(true);
        lastScrollY.current = currentScrollY;
        return;
      }

      if (currentScrollY <= 0) {
        setIsNavVisible(true);
      } else if (currentScrollY > lastScrollY.current + 6) {
        setIsNavVisible(false);
      } else if (currentScrollY < lastScrollY.current - 6) {
        setIsNavVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    lastScrollY.current = window.scrollY;
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMenuOpen]);

  return (
    <nav
      className={`bg-background sticky top-0 isolate z-50 py-3.5 transition-transform duration-300 ${isAtTop ? "md:py-6" : "md:py-4"} ${
        isNavVisible ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      <div className="container-padding-x container m-auto">
        {/* Main navbar row */}
        <div className="flex items-center">
          <Link href="/" aria-label="Go to homepage">
            <SqarpLogo className="h-[1.275rem] w-auto text-black md:h-5.5 lg:h-6" />
          </Link>

          {/* Desktop menu */}
          {hasDesktopMenu ? (
            <div className="absolute left-1/2 hidden -translate-x-1/2 lg:flex">
              <NavMenuItems
                solutionsLabel={solutionsLabel}
                solutionsItems={solutionMenuItems}
                mainMenuItems={primaryMenuItems}
              />
            </div>
          ) : null}

          <div className="ml-auto flex items-center gap-2 md:gap-4">
            {loginLink ? (
              <Button
                asChild
                variant="link"
                size="link-xl"
                className="font-geist text-black max-xs:hidden"
              >
                <Link href={loginLink.href}>
                  <ResponsiveLabel label={loginLink.label} />
                </Link>
              </Button>
            ) : null}
            {ctaAction ? (
              <Button asChild size="lg" className="max-xs:hidden">
                <Link href={ctaAction.href}>
                  <ResponsiveLabel
                    label={ctaAction.label}
                    mobileLabel={ctaAction.mobileLabel}
                  />
                </Link>
              </Button>
            ) : null}

            {/* Mobile menu button */}
            {hasMobileMenuContent ? (
              <Button
                variant="ghost"
                className="flex size-10 items-center justify-center lg:hidden"
                onClick={toggleMenu}
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMenuOpen ? (
                  <X className="size-6" />
                ) : (
                  <Menu className="size-6" />
                )}
              </Button>
            ) : null}
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && hasMobileMenuContent && (
          <div className="flex flex-col gap-2.5 pt-4 lg:hidden">
            <MobileSolutionsItems
              solutionsLabel={solutionsLabel}
              solutionsItems={solutionMenuItems}
              onNavigate={closeMenu}
            />
            {primaryMenuItems?.map(({ label, href }) => (
              <Button
                key={label}
                asChild
                variant="ghost"
                className="w-full justify-start max-md:text-lg"
              >
                <Link href={href} className="hover:underline" onClick={closeMenu}>
                  {label}
                </Link>
              </Button>
            ))}
            {loginLink || ctaAction ? <Separator className="my-2" /> : null}
            <div className="hidden max-xs:flex flex-col gap-3">
              {loginLink ? (
                <Button asChild variant="ghost" className="w-full font-geist">
                  <Link
                    href={loginLink.href}
                    className="w-full"
                    onClick={closeMenu}
                  >
                    <ResponsiveLabel label={loginLink.label} />
                  </Link>
                </Button>
              ) : null}
              {ctaAction ? (
                <Button asChild className="w-full">
                  <Link
                    href={ctaAction.href}
                    className="w-full"
                    onClick={closeMenu}
                  >
                    <ResponsiveLabel
                      label={ctaAction.label}
                      mobileLabel={ctaAction.mobileLabel}
                    />
                  </Link>
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
